import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Share2, Copy, Mail, MessageSquare, ExternalLink, Users } from 'lucide-react';

const ShareModal = ({ isOpen, onClose, selectedFriends, onShare }) => {
  const [shareData, setShareData] = useState({
    message: '',
    share_type: 'link'
  });
  const [shareResult, setShareResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedFriends.length === 0) return;
    
    setIsLoading(true);
    try {
      const result = await onShare({
        friend_ids: selectedFriends.map(f => f.id),
        message: shareData.message,
        share_type: shareData.share_type
      });
      setShareResult(result);
    } catch (error) {
      console.error('Error sharing friends:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setShareData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
      alert('Copied to clipboard!');
    });
  };

  const shareViaEmail = () => {
    if (!shareResult) return;
    
    const subject = `Trusted Professional Recommendations`;
    const body = shareResult.content + `\n\nView online: ${shareResult.share_link}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const shareViaSMS = () => {
    if (!shareResult) return;
    
    const message = `Check out these trusted professionals! ${shareResult.share_link}`;
    window.location.href = `sms:?body=${encodeURIComponent(message)}`;
  };

  const handleClose = () => {
    setShareResult(null);
    setShareData({ message: '', share_type: 'link' });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" style={{color: 'var(--brand-navy)'}} />
            Share Trusted Professionals ({selectedFriends.length})
          </DialogTitle>
        </DialogHeader>

        {!shareResult ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Selected Friends Preview */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4" />
                Selected Professionals
              </Label>
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                {selectedFriends.map(friend => (
                  <Badge key={friend.id} variant="secondary" className="bg-white">
                    {friend.name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <Label htmlFor="message">Custom Message (Optional)</Label>
              <Textarea
                id="message"
                value={shareData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                placeholder="Add a personal note about these professionals..."
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Share Type */}
            <div>
              <Label>How would you like to share?</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={shareData.share_type === 'link' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleInputChange('share_type', 'link')}
                  className="flex-1"
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Link
                </Button>
                <Button
                  type="button"
                  variant={shareData.share_type === 'email' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleInputChange('share_type', 'email')}
                  className="flex-1"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </Button>
                <Button
                  type="button"
                  variant={shareData.share_type === 'text' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleInputChange('share_type', 'text')}
                  className="flex-1"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Text
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={selectedFriends.length === 0 || isLoading}
                className="flex-1 text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-dark) 100%)'
                }}
              >
                {isLoading ? 'Creating...' : 'Generate Share Link'}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">
                  Share link created successfully!
                </span>
              </div>
              <p className="text-sm text-green-600">
                {shareResult.friends_count} professionals ready to share
              </p>
            </div>

            {/* Share Link */}
            <div>
              <Label>Share Link</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={shareResult.share_link}
                  readOnly
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => copyToClipboard(shareResult.share_link)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Share Actions */}
            <div>
              <Label>Quick Share</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={shareViaEmail}
                  className="flex-1"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={shareViaSMS}
                  className="flex-1"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Text Message
                </Button>
              </div>
            </div>

            {/* Preview Content */}
            <div>
              <Label>Preview Content</Label>
              <div className="mt-1 p-3 bg-gray-50 rounded-lg max-h-40 overflow-y-auto">
                <pre className="text-sm whitespace-pre-wrap text-gray-700">
                  {shareResult.content}
                </pre>
              </div>
            </div>

            <div className="flex gap-2">
              <Button 
                type="button" 
                onClick={handleClose}
                className="w-full"
                style={{
                  background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-dark) 100%)'
                }}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;